/**
 * Route-level permission guard.
 *
 * The sidebar already hides modules an admin cannot use, but a typed URL or a
 * stale bookmark bypasses the sidebar — so every protected route repeats the
 * check here. (The backend enforces it a third time; that is the one that
 * actually matters. These two exist so staff get a sensible screen instead of a
 * wall of 403s.)
 */
import { Show, createEffect, type JSX, type ParentProps } from "solid-js";
import { useNavigate } from "@solidjs/router";

import { useSession, type SessionValue } from "~/core/auth/session";
import { NAV_FOOTER, NAV_SECTIONS } from "~/layout/nav-config";
import type { Permission } from "./permissions";

/** The first destination this admin is actually allowed to open. */
export function firstAllowedPath(session: SessionValue): string | null {
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (session.can(item.permission)) return item.href;
    }
  }
  for (const item of NAV_FOOTER) {
    if (session.can(item.permission)) return item.href;
  }
  return null;
}

export function RequirePermission(
  props: ParentProps<{ perform: Permission }>,
): JSX.Element {
  const session = useSession();
  const navigate = useNavigate();

  createEffect(() => {
    if (!session.ready() || !session.signedIn()) return;
    if (session.can(props.perform)) return;
    // Send them somewhere they can actually work, or to the dead-end screen if
    // their role grants nothing at all.
    const fallback = firstAllowedPath(session);
    navigate(fallback ?? "/no-access", { replace: true });
  });

  return <Show when={session.can(props.perform)}>{props.children}</Show>;
}
